import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { userConfigService } from '../services/userConfigService';
import authService from '../services/auth';

const UserConfigContext = createContext();

export const AVAILABLE_TARIFFS = [
  { value: 'governmental', label: 'Gubernamental Cat. 10', description: 'Tarifa única del sistema' }
];

export function UserConfigProvider({ children }) {
  // Cargar valores iniciales desde localStorage o valores por defecto
  const [householdMembers, setHouseholdMembers] = useState(() => {
    const saved = localStorage.getItem('householdMembers');
    return saved ? parseInt(saved) : 1;
  });
  const [dailyGoal, setDailyGoal] = useState(() => {
    const saved = localStorage.getItem('dailyGoal');
    return saved ? parseInt(saved) : '';
  });
  const [selectedTariff, setSelectedTariff] = useState(() => {
    const saved = localStorage.getItem('selectedTariff');
    return saved || 'governmental';
  });

  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const initialLoad = useRef(true);

  // Cargar preferencias desde AWS al iniciar sesión
  useEffect(() => {
    const loadPreferences = async () => {
      if (!authService.isAuthenticated()) {
        console.log('⚠️ No autenticado, usando valores locales');
        setIsLoading(false);
        initialLoad.current = false;
        return;
      }

      try {
        console.log('🔄 Cargando preferencias desde AWS...');
        const prefs = await userConfigService.syncPreferences();
        
        if (prefs) {
          console.log('✅ Preferencias cargadas desde AWS:', prefs);
          if (prefs.household_members !== undefined) {
            setHouseholdMembers(prefs.household_members);
          }
          if (prefs.daily_goal !== undefined && prefs.daily_goal !== '') {
            setDailyGoal(prefs.daily_goal);
          }
          if (prefs.selected_tariff !== undefined) {
            setSelectedTariff(prefs.selected_tariff);
          }
        }
      } catch (error) {
        console.error('❌ Error cargando preferencias:', error);
        // Continuar con localStorage
      } finally {
        setIsLoading(false);
        // ✅ IMPORTANTE: Esperar un tick para permitir que los estados se actualicen
        setTimeout(() => {
          initialLoad.current = false;
          console.log('✅ Carga inicial completada, sincronización habilitada');
        }, 100);
      }
    };

    loadPreferences();
    /* Y al cambiar de sesion: este proveedor tambien se monta antes del login,
       asi que sin esto las preferencias de la cuenta no se cargaban hasta
       recargar la pagina. Ver DeviceContext. */
    return authService.onSessionChange(() => {
      initialLoad.current = true;   // evita que la carga dispare un guardado
      loadPreferences();
    });
  }, []);

  // Sincronizar preferencias con AWS cuando cambien
  useEffect(() => {
    console.log('🔍 useEffect sync disparado:', { 
      initialLoad: initialLoad.current, 
      isLoading, 
      householdMembers, 
      dailyGoal, 
      selectedTariff 
    });

    // No sincronizar en la carga inicial
    if (initialLoad.current || isLoading) {
      console.log('⏸️ Sincronización bloqueada (carga inicial o loading)');
      return;
    }

    const syncToAWS = async () => {
      console.log('💾 syncToAWS ejecutándose...');
      
      // Guardar en localStorage inmediatamente
      localStorage.setItem('householdMembers', householdMembers.toString());
      if (dailyGoal !== '') {
        localStorage.setItem('dailyGoal', dailyGoal.toString());
      }
      localStorage.setItem('selectedTariff', selectedTariff);
      console.log('✅ Guardado en localStorage');

      // Solo sincronizar con AWS si está autenticado
      const isAuth = authService.isAuthenticated();
      console.log('🔐 Usuario autenticado:', isAuth);
      
      if (!isAuth) {
        console.log('ℹ️ Usuario no autenticado, guardado solo en localStorage');
        return;
      }

      setIsSyncing(true);
      try {
        console.log('🔄 Sincronizando preferencias con AWS...');
        await userConfigService.updateUserPreferences({
          household_members: householdMembers,
          daily_goal: dailyGoal === '' ? null : dailyGoal,
          selected_tariff: selectedTariff
        });
        console.log('✅ Preferencias sincronizadas con AWS');
      } catch (error) {
        console.error('❌ Error sincronizando con AWS:', error);
        // Ya guardamos en localStorage, continuar sin error
      } finally {
        setIsSyncing(false);
      }
    };

    // Debounce para evitar múltiples llamadas simultáneas
    const timeoutId = setTimeout(syncToAWS, 500);
    return () => clearTimeout(timeoutId);
  }, [householdMembers, dailyGoal, selectedTariff, isLoading]);

  return (
    <UserConfigContext.Provider value={{
      householdMembers,
      setHouseholdMembers,
      dailyGoal,
      setDailyGoal,
      selectedTariff,
      setSelectedTariff,
      AVAILABLE_TARIFFS,
      isSyncing,
      isLoading
    }}>
      {children}
    </UserConfigContext.Provider>
  );
}

export function useUserConfig() {
  const context = useContext(UserConfigContext);
  if (!context) {
    throw new Error('useUserConfig must be used within a UserConfigProvider');
  }
  return context;
} 